import React, { useState, useEffect } from 'react';
import { urls } from './config.dev';
import { useNavigate } from 'react-router-dom';
import UpdateStocksModal from './UpdateStocksModal';
import Topbar from './Topbar';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const colors = {
  blue: {
    sidebarBg: '#0a1e4a',
    sidebarBorder: '#1e3a8a',
    activeNavBg: '#2563eb',
    activeNavText: '#ffffff',
    inactiveNavText: '#e0e7ff',
    navHoverBg: '#1e3a8a',
    sectionHeaderText: '#94a3b8',
    mainBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    accent: '#16a34a',
    accentLight: '#dcfce7',
    danger: '#dc2626',
    dangerLight: '#fef2f2',
    warning: '#d97706',
    warningLight: '#fffbeb',
    info: '#2563eb',
    infoLight: '#eff6ff',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    tableHeader: '#f1f5f9',
    tableBorder: '#e2e8f0',
    filterSection: '#0d2257',
    collapseButtonBg: '#1e3a8a',
    collapseButtonHover: '#2563eb',
    collapseButtonText: '#ffffff',
    tooltipBg: '#1e293b',
    tooltipText: '#ffffff',
    iconBright: '#fbbf24',
    iconHover: '#f59e0b',
    sidebarText: '#ffffff',
    sidebarTextMuted: '#a5b4fc',
    staffCardBg: 'rgba(255,255,255,0.05)',
    staffCardBorder: '#1e3a8a',
    logoBg: '#2563eb',
    inputBg: '#ffffff',
    inputBorder: '#e2e8f0',
    inputText: '#0f172a',
    selectBg: '#ffffff',
    selectText: '#0f172a',
  },
  white: {
    sidebarBg: '#ffffff',
    sidebarBorder: '#e2e8f0',
    activeNavBg: '#f1f5f9',
    activeNavText: '#0f172a',
    inactiveNavText: '#475569',
    navHoverBg: '#f8fafc',
    sectionHeaderText: '#64748b',
    mainBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    accent: '#16a34a',
    accentLight: '#dcfce7',
    danger: '#dc2626',
    dangerLight: '#fef2f2',
    warning: '#d97706',
    warningLight: '#fffbeb',
    info: '#2563eb',
    infoLight: '#eff6ff',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    tableHeader: '#f1f5f9',
    tableBorder: '#e2e8f0',
    filterSection: '#f8fafc',
    collapseButtonBg: '#e2e8f0',
    collapseButtonHover: '#cbd5e1',
    collapseButtonText: '#0f172a',
    tooltipBg: '#1e293b',
    tooltipText: '#ffffff',
    iconBright: '#f59e0b',
    iconHover: '#d97706',
    sidebarText: '#0f172a',
    sidebarTextMuted: '#64748b',
    staffCardBg: '#f8fafc',
    staffCardBorder: '#e2e8f0',
    logoBg: '#2563eb',
    inputBg: '#ffffff',
    inputBorder: '#e2e8f0',
    inputText: '#0f172a',
    selectBg: '#ffffff',
    selectText: '#0f172a',
  }
};

// ─── MOBILE DETECTION HOOK ───────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ─── SHARED STYLES ────────────────────────────────────────────────────────────
const styles = (theme) => ({
  card: {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardMobile: {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: theme.cardBg,
    borderRadius: '12px',
    overflow: 'hidden',
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    background: theme.tableHeader,
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: `2px solid ${theme.tableBorder}`,
  },
  td: {
    padding: '14px 16px',
    borderBottom: `1px solid ${theme.tableBorder}`,
    color: theme.textPrimary,
    fontSize: '13px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid ${theme.inputBorder}`,
    background: theme.inputBg,
    color: theme.inputText,
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: `1px solid ${theme.inputBorder}`,
    background: theme.selectBg,
    color: theme.selectText,
    fontSize: '14px',
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box',
  },
  actionButton: {
    padding: '7px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
  },
});

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GlobalStyles = ({ theme }) => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
    ::-webkit-scrollbar { width: 5px; height: 5px; }
    ::-webkit-scrollbar-track { background: ${theme.tableHeader}; }
    ::-webkit-scrollbar-thumb { background: ${theme.textMuted}; border-radius: 4px; }
    .nav-item:hover { background: ${theme.navHoverBg} !important; }
    .nav-icon { color: ${theme.iconBright}; font-size: 18px; transition: all 0.2s ease; }
    .collapse-btn:hover { background: ${theme.collapseButtonHover} !important; }
    .table-row:hover { background: ${theme.tableHeader} !important; }
    .mobile-item-card { transition: box-shadow 0.2s ease; }
    .mobile-item-card:active { box-shadow: 0 0 0 2px ${theme.info} !important; }
    .mobile-nav-tab.active { border-bottom: 3px solid ${theme.activeNavBg}; color: ${theme.activeNavBg}; }
    @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; animation: fadeIn 0.2s ease; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .spin { animation: spin 1s linear infinite; }
    @media (max-width: 768px) {
      .desktop-only { display: none !important; }
      .mobile-only { display: flex !important; }
    }
    @media (min-width: 769px) {
      .mobile-only { display: none !important; }
      .desktop-only { display: flex !important; }
    }
  `}</style>
);

// ─── LOADING SPINNER ─────────────────────────────────────────────────────────
function LoadingSpinner({ theme }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '16px' }}>
      <div style={{ width: '36px', height: '36px', border: `3px solid ${theme.tableBorder}`, borderTopColor: theme.accent, borderRadius: '50%' }} className="spin"></div>
      <div style={{ color: theme.textMuted, fontSize: '13px', letterSpacing: '0.05em' }}>PLEASE WAIT...</div>
    </div>
  );
}

// ─── DELETE MODAL ─────────────────────────────────────────────────────────────
function DeleteConfirmationModal({ isOpen, onClose, onConfirm, drug, theme, isMobile }) {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        style={{
          background: theme.cardBg, borderRadius: '16px',
          width: isMobile ? '94vw' : '90%', maxWidth: '480px',
          padding: isMobile ? '20px' : '28px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.25s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ width: '56px', height: '56px', background: theme.dangerLight, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '28px' }}>⚠️</div>
        <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', color: theme.danger, textAlign: 'center', marginBottom: '12px' }}>ADMIN ACTION REQUIRED</h2>
        <div style={{ background: theme.warningLight, border: `1px solid ${theme.warning}`, borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '6px' }}>You are about to delete:</div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: theme.textPrimary }}>{drug?.drug_name}</div>
          <div style={{ fontSize: '13px', color: theme.textMuted, marginTop: '3px' }}>Packaging: {drug?.packaging}</div>
        </div>
        <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[['🔴', theme.danger, 'Permanently remove this item from the system'],
            ['⚠️', theme.warning, 'Affects existing stock in all locations'],
            ['📊', theme.warning, 'Impacts historical records and transactions']
          ].map(([icon, color, text]) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '10px', color, fontSize: '13px', fontWeight: '500' }}>
              <span>{icon}</span>{text}
            </div>
          ))}
        </div>
        <div style={{ background: theme.infoLight, border: `1px solid ${theme.info}`, borderRadius: '8px', padding: '11px', marginBottom: '20px', fontSize: '12px', color: theme.textSecondary }}>
          <span style={{ fontWeight: '600', color: theme.info }}>ℹ️ Note:</span> Only proceed with administrator approval.
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: '8px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textSecondary, fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex: 1, padding: '11px', borderRadius: '8px', border: 'none', background: theme.danger, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Confirm Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── MOBILE ITEM CARD ─────────────────────────────────────────────────────────
function MobileItemCard({ drug, theme, onEdit, onDelete }) {
  return (
    <div className="mobile-item-card" style={{
      background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
      borderRadius: '12px', padding: '16px', marginBottom: '10px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ flex: 1, paddingRight: '12px' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: theme.textPrimary, marginBottom: '3px' }}>{drug.drug_name}</div>
          <div style={{ fontSize: '12px', color: theme.textMuted, background: theme.tableHeader, display: 'inline-block', padding: '2px 8px', borderRadius: '20px' }}>{drug.packaging}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button onClick={() => onEdit(drug)} style={{ width: '34px', height: '34px', borderRadius: '8px', border: `1px solid ${theme.info}`, background: theme.infoLight, color: theme.info, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✏️</button>
          <button onClick={() => onDelete(drug)} style={{ width: '34px', height: '34px', borderRadius: '8px', border: `1px solid ${theme.danger}`, background: theme.dangerLight, color: theme.danger, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🗑️</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
        {[
          ['Warning Pt.', drug.warning_point],
          ['Cost', `UGX ${Number(drug.cost_price).toLocaleString()}`],
          ['Selling', `UGX ${Number(drug.selling_price).toLocaleString()}`],
        ].map(([label, value]) => (
          <div key={label} style={{ background: theme.tableHeader, borderRadius: '8px', padding: '8px 10px' }}>
            <div style={{ fontSize: '10px', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{label}</div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: theme.textPrimary }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MOBILE BOTTOM NAV ────────────────────────────────────────────────────────
function MobileBottomNav({ activeView, onNavigate, itemCount, theme }) {
  const tabs = [
    { id: 'list', icon: '📦', label: 'Items', badge: itemCount },
    { id: 'create', icon: '➕', label: 'Add New' },
  ];
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: theme.cardBg, borderTop: `1px solid ${theme.cardBorder}`,
      display: 'flex', zIndex: 200,
      boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onNavigate(tab.id)}
          style={{
            flex: 1, padding: '10px 8px 8px', border: 'none', background: 'transparent',
            borderTop: activeView === tab.id ? `3px solid ${theme.activeNavBg}` : '3px solid transparent',
            cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
            transition: 'all 0.15s ease',
          }}
        >
          <span style={{ fontSize: '20px', position: 'relative' }}>
            {tab.icon}
            {tab.badge > 0 && (
              <span style={{ position: 'absolute', top: '-4px', right: '-8px', background: theme.accent, color: '#fff', fontSize: '9px', fontWeight: '700', padding: '1px 4px', borderRadius: '10px', minWidth: '16px', textAlign: 'center' }}>{tab.badge}</span>
            )}
          </span>
          <span style={{ fontSize: '11px', fontWeight: activeView === tab.id ? '600' : '400', color: activeView === tab.id ? theme.activeNavBg : theme.textMuted }}>{tab.label}</span>
        </button>
      ))}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
function DrugOrigins() {
  const isMobile = useIsMobile();

  const [newDrug, setNewDrug] = useState({ drugName: '', packaging: '', warningPoint: '', costPrice: '', sellingPrice: '' });
  const [allDrugs, setAllDrugs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingDrug, setIsAddingDrug] = useState(false);
  const [employeeName, setEmployeeName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [drugToDelete, setDrugToDelete] = useState(null);
  const [token, setToken] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [currentTheme, setCurrentTheme] = useState('blue');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [activeView, setActiveView] = useState('list');
  const [loading, setLoading] = useState(true);
  const [useDrugExpiryDate, setUseDrugExpiryDate] = useState('yes');
  const [useDrugBatchNumbers, setUseDrugBatchNumbers] = useState('yes');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  const theme = colors[currentTheme];

  // Security check
  useEffect(() => {
    if (!urlToken) { navigate('/login'); return; }
    const checkSecurity = async () => {
      try {
        const res = await fetch(urls.security, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: urlToken }) });
        if (!res.ok) throw new Error('Security check failed');
        const data = await res.json();
        if (data.message === 'Session valid') {
          setEmployeeName(data.employee_name);
          setClinicName(data.clinic);
          const tc = data.colour || '';
          setCurrentTheme((!tc || tc.toLowerCase() === 'white' || tc.toLowerCase() === 'null') ? 'white' : 'blue');
          setUseDrugExpiryDate(data.use_drug_expiry_date || 'yes');
          setUseDrugBatchNumbers(data.use_drug_batch_numbers || 'yes');
          setToken(urlToken);
          await fetchAllDrugs(urlToken);
          setLoading(false);
        } else if (data.error === 'Session expired') {
          navigate(`/dashboard?token=${data.clinic_session_token}`);
        } else {
          navigate('/login');
        }
      } catch (e) {
        navigate('/login');
      }
    };
    checkSecurity();
  }, [navigate, urlToken]);

  // Auto-refresh
  useEffect(() => {
    if (token) {
      const interval = setInterval(() => fetchAllDrugs(token), 10000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchAllDrugs = async (t) => {
    try {
      const res = await fetch(urls.fetchoriginaldrugs, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: t }) });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setAllDrugs(data.sort((a, b) => a.drug_name.localeCompare(b.drug_name)));
    } catch (e) { console.error(e); }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDrug(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => setNewDrug({ drugName: '', packaging: '', warningPoint: '', costPrice: '', sellingPrice: '' });

  const handleCreateDrug = () => {
    if (!newDrug.drugName || !newDrug.packaging || !newDrug.warningPoint || !newDrug.costPrice || !newDrug.sellingPrice) {
      alert("⚠️ Please fill in all fields."); return;
    }
    const exists = allDrugs.find(d => d.drug_name.toLowerCase() === newDrug.drugName.toLowerCase() && d.packaging.toLowerCase() === newDrug.packaging.toLowerCase());
    if (exists) { alert("❌ This item with the same name and packaging already exists."); return; }
    setIsAddingDrug(true);
    fetch(urls.adddrug, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...newDrug, token }) })
      .then(r => { if (!r.ok) throw new Error(); resetForm(); fetchAllDrugs(token); setActiveView('list'); alert("✅ Item created successfully!"); })
      .catch(() => alert("❌ Failed to create item. Please try again."))
      .finally(() => setIsAddingDrug(false));
  };

  const handleDeleteClick = (drug) => { setDrugToDelete(drug); setShowDeleteModal(true); };

  const handleConfirmDelete = () => {
    if (!drugToDelete) return;
    setIsAddingDrug(true); setShowDeleteModal(false);
    fetch(urls.deleteoriginaldrug, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ drugName: drugToDelete.drug_name, packaging: drugToDelete.packaging, drug_id: drugToDelete.drug_id, token }) })
      .then(r => { if (!r.ok) throw new Error(); fetchAllDrugs(token); alert("✅ Item deleted successfully."); })
      .catch(() => alert("❌ Failed to delete. Check your permissions."))
      .finally(() => { setIsAddingDrug(false); setDrugToDelete(null); });
  };

  const handleOpenModal = (drug) => {
    setSelectedDrug({ drug_id: drug.drug_id, drug_name: drug.drug_name, packaging: drug.packaging, warning_point: drug.warning_point, cost_price: drug.cost_price, selling_price: drug.selling_price, average: drug.average, status: drug.status, Last_sold: drug.Last_sold, additional_info: drug.additional_info, units: drug.units, unit_packaging: drug.unit_packaging, theme: currentTheme, use_drug_expiry_date: useDrugExpiryDate, use_drug_batch_numbers: useDrugBatchNumbers });
    setShowModal(true);
  };

  const initials = (name) => {
    if (!name) return 'A';
    const p = name.trim().split(' ');
    return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name[0].toUpperCase();
  };

  const filteredDrugs = allDrugs.filter(d => d.drug_name.toLowerCase().includes(searchQuery.toLowerCase()));

  // ── MOBILE LAYOUT ──────────────────────────────────────────────────────────
  if (isMobile) {
    if (loading) {
      return (
        <>
          <GlobalStyles theme={theme} />
          <div style={{ minHeight: '100vh', background: theme.mainBg }}>
            <Topbar token={urlToken} themeColor={currentTheme} />
            <LoadingSpinner theme={theme} />
          </div>
        </>
      );
    }

    return (
      <>
        <GlobalStyles theme={theme} />
        <div style={{ minHeight: '100vh', background: theme.mainBg, paddingBottom: '80px' }}>
          <Topbar token={urlToken} themeColor={currentTheme} />

          {/* Mobile Header */}
          <div style={{
            background: theme.cardBg, borderBottom: `1px solid ${theme.cardBorder}`,
            padding: '12px 16px', position: 'sticky', top: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: theme.textPrimary }}>
                {activeView === 'list' ? '📦 System Items' : '➕ Add New Item'}
              </div>
              <div style={{ fontSize: '11px', color: theme.textMuted }}>{clinicName}</div>
            </div>
            {activeView === 'list' && (
              <div style={{ fontSize: '12px', background: theme.accentLight, color: theme.accent, padding: '4px 10px', borderRadius: '20px', fontWeight: '600' }}>
                {filteredDrugs.length} items
              </div>
            )}
          </div>

          {/* Content */}
          <div style={{ padding: '12px 14px' }}>
            {activeView === 'list' && (
              <>
                {/* Search */}
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Search items..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ ...styles(theme).input, fontSize: '15px', padding: '12px 14px' }}
                  />
                </div>

                {/* Items */}
                {filteredDrugs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '50px 20px', color: theme.textMuted }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
                    <div style={{ fontSize: '14px' }}>
                      {allDrugs.length === 0 ? 'No items yet. Tap ➕ to add your first item.' : 'No items match your search.'}
                    </div>
                  </div>
                ) : (
                  filteredDrugs.map((drug, i) => (
                    <MobileItemCard key={drug.drug_id || i} drug={drug} theme={theme} onEdit={handleOpenModal} onDelete={handleDeleteClick} />
                  ))
                )}
              </>
            )}

            {activeView === 'create' && (
              <div style={{ ...styles(theme).cardMobile, animation: 'slideIn 0.25s ease' }}>
                <h2 style={{ fontSize: '17px', fontWeight: '700', color: theme.textPrimary, marginBottom: '6px' }}>Add New Item</h2>
                <p style={{ color: theme.textMuted, fontSize: '13px', marginBottom: '20px' }}>This item will be available across all business modules.</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {[
                    { label: 'Item Name', name: 'drugName', type: 'text', placeholder: 'e.g., Colgate, Soda' },
                    { label: 'Warning Point', name: 'warningPoint', type: 'number', placeholder: 'Minimum stock alert level' },
                    { label: 'Cost Price (UGX)', name: 'costPrice', type: 'number', placeholder: 'Purchase price per unit' },
                    { label: 'Selling Price (UGX)', name: 'sellingPrice', type: 'number', placeholder: 'Selling price' },
                  ].map(field => (
                    <div key={field.name}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textMuted, marginBottom: '6px' }}>
                        {field.label} <span style={{ color: theme.danger }}>*</span>
                      </label>
                      <input type={field.type} name={field.name} value={newDrug[field.name]} onChange={handleInputChange} placeholder={field.placeholder} style={{ ...styles(theme).input, padding: '12px 14px', fontSize: '15px' }} />
                    </div>
                  ))}

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textMuted, marginBottom: '6px' }}>
                      Packaging <span style={{ color: theme.danger }}>*</span>
                    </label>
                    <select name="packaging" value={newDrug.packaging} onChange={handleInputChange} style={{ ...styles(theme).select, padding: '12px 14px', fontSize: '15px' }}>
                      <option value="">Select Packaging</option>
                      {['Boxes','Packets','Sackets','Pieces','Rolls','Bottles','kgs','ltrs'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                {/* Info Banner */}
                <div style={{ background: theme.warningLight, border: `1px solid ${theme.warning}`, borderRadius: '8px', padding: '12px', marginTop: '20px', fontSize: '12px', color: theme.warning, lineHeight: '1.5' }}>
                  <strong>⚠️ Administrator Notice:</strong> Item name + packaging must be unique. Ensure you have admin privileges.
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button onClick={() => { resetForm(); setActiveView('list'); }} style={{ flex: 1, padding: '13px', borderRadius: '8px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textSecondary, fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={handleCreateDrug} disabled={isAddingDrug} style={{ flex: 2, padding: '13px', borderRadius: '8px', border: 'none', background: isAddingDrug ? theme.textMuted : theme.accent, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: isAddingDrug ? 'not-allowed' : 'pointer', opacity: isAddingDrug ? 0.7 : 1 }}>
                    {isAddingDrug ? 'Creating...' : '✅ Create Item'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Bottom Nav */}
          <MobileBottomNav activeView={activeView} onNavigate={(v) => { if (v === 'create') resetForm(); setActiveView(v); }} itemCount={allDrugs.length} theme={theme} />
        </div>

        {/* Modals */}
        <DeleteConfirmationModal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setDrugToDelete(null); }} onConfirm={handleConfirmDelete} drug={drugToDelete} theme={theme} isMobile={true} />
        {showModal && selectedDrug && (
          <UpdateStocksModal isOpen={showModal} onClose={() => { setShowModal(false); setSelectedDrug(null); }} selectedDrug={selectedDrug} token={token} refreshDrugs={() => fetchAllDrugs(token)} themeColor={currentTheme} />
        )}
      </>
    );
  }

  // ── DESKTOP LAYOUT ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <GlobalStyles theme={theme} />
        <div style={{ display: 'flex', minHeight: '100vh', background: theme.mainBg }}>
          <aside style={{ width: '260px', background: theme.sidebarBg, position: 'fixed', top: 0, left: 0, bottom: 0 }} />
          <main style={{ marginLeft: '260px', flex: 1 }}>
            <Topbar token={urlToken} themeColor={currentTheme} />
            <div style={{ padding: '28px' }}><LoadingSpinner theme={theme} /></div>
          </main>
        </div>
      </>
    );
  }

  const navSections = [
    {
      label: 'ITEM MANAGEMENT',
      items: [
        { id: 'system-drugs', icon: '💊', label: 'System Items', action: () => setActiveView('list'), badge: allDrugs.length },
        { id: 'create-drug', icon: '➕', label: 'Create New Item', action: () => { resetForm(); setActiveView('create'); } },
      ],
    },
  ];

  return (
    <>
      <GlobalStyles theme={theme} />
      <div style={{ display: 'flex', minHeight: '100vh', background: theme.mainBg }}>

        {/* Sidebar */}
        <aside style={{
          width: sidebarCollapsed ? '80px' : '260px',
          background: theme.sidebarBg,
          display: 'flex', flexDirection: 'column', flexShrink: 0,
          borderRight: `1px solid ${theme.sidebarBorder}`,
          position: 'fixed', paddingTop: '80px',
          top: 0, left: 0, bottom: 0,
          overflowY: 'auto',
          boxShadow: currentTheme === 'blue' ? '2px 0 8px rgba(0,0,0,0.1)' : '2px 0 8px rgba(0,0,0,0.05)',
          transition: 'width 0.3s ease', zIndex: 900,
        }}>
          {/* Logo + Collapse */}
          <div style={{
            padding: sidebarCollapsed ? '20px 10px' : '20px 16px',
            borderBottom: `2px solid ${theme.sidebarBorder}`,
            display: 'flex', flexDirection: sidebarCollapsed ? 'column' : 'row',
            alignItems: 'center', justifyContent: 'space-between',
            background: currentTheme === 'blue' ? 'rgba(0,0,0,0.2)' : theme.filterSection,
            minHeight: sidebarCollapsed ? '120px' : '80px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, order: sidebarCollapsed ? 2 : 1, marginTop: sidebarCollapsed ? '12px' : 0 }}>
              <div style={{ width: '45px', height: '45px', background: theme.logoBg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0, color: '#fff', fontWeight: 'bold' }}>CP</div>
              {!sidebarCollapsed && (
                <div>
                  <div style={{ color: theme.sidebarText, fontWeight: '700', fontSize: '15px' }}>SET ITEMS</div>
                  <div style={{ color: theme.sidebarTextMuted, fontSize: '11px', fontWeight: '500', marginTop: '2px' }}>ITEM MANAGEMENT</div>
                </div>
              )}
            </div>
            <button className="collapse-btn" onClick={() => setSidebarCollapsed(!sidebarCollapsed)} style={{
              background: theme.collapseButtonBg, border: 'none', color: theme.collapseButtonText,
              width: '40px', height: '40px', borderRadius: '8px', cursor: 'pointer', fontSize: '18px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              order: sidebarCollapsed ? 1 : 2, marginLeft: sidebarCollapsed ? 0 : 'auto', flexShrink: 0,
            }}>
              {sidebarCollapsed ? '→' : '←'}
            </button>
          </div>

          {/* Staff Card */}
          {!sidebarCollapsed && employeeName && (
            <div style={{ margin: '16px 16px 20px', background: theme.staffCardBg, borderRadius: '10px', padding: '14px 16px', border: `1px solid ${theme.staffCardBorder}` }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: `linear-gradient(135deg, ${theme.accent}, ${theme.logoBg})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: '#fff', marginBottom: '10px' }}>{initials(employeeName)}</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: theme.sidebarText }}>{employeeName}</div>
              <div style={{ fontSize: '11px', color: theme.sidebarTextMuted, marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Administrator</div>
            </div>
          )}

          {/* Nav */}
          <nav style={{ flex: 1, padding: sidebarCollapsed ? '12px 0' : '8px 12px', overflowY: 'auto' }}>
            {navSections.map(section => (
              <div key={section.label} style={{ marginBottom: '16px' }}>
                {!sidebarCollapsed && <div style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: theme.sectionHeaderText, padding: '0 14px', marginBottom: '8px', marginTop: '12px' }}>{section.label}</div>}
                {section.items.map(item => {
                  const isActive = (item.id === 'system-drugs' && activeView === 'list') || (item.id === 'create-drug' && activeView === 'create');
                  return (
                    <button key={item.id} className={`nav-item ${isActive ? 'active-tab' : ''}`} onClick={item.action}
                      onMouseEnter={() => setHoveredNavItem(item.id)} onMouseLeave={() => setHoveredNavItem(null)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', gap: sidebarCollapsed ? '0' : '12px', padding: sidebarCollapsed ? '12px 0' : '10px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13.5px', fontWeight: isActive ? '600' : '500', color: isActive ? theme.activeNavText : theme.inactiveNavText, background: isActive ? theme.activeNavBg : 'transparent', border: 'none', width: '100%', textAlign: sidebarCollapsed ? 'center' : 'left', marginBottom: '2px', position: 'relative' }}>
                      <span style={{ fontSize: '18px', width: '20px', textAlign: 'center', color: isActive ? theme.activeNavText : theme.iconBright }}>{item.icon}</span>
                      {!sidebarCollapsed && (
                        <>
                          <span style={{ fontWeight: '500', flex: 1, textAlign: 'left', color: isActive ? theme.activeNavText : theme.sidebarText }}>{item.label}</span>
                          {item.badge > 0 && <span style={{ background: theme.accent, color: '#fff', fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '10px', minWidth: '18px', textAlign: 'center' }}>{item.badge}</span>}
                        </>
                      )}
                      {sidebarCollapsed && hoveredNavItem === item.id && (
                        <div style={{ position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '12px', padding: '8px 12px', background: theme.tooltipBg, color: theme.tooltipText, fontSize: '12px', fontWeight: '500', borderRadius: '6px', whiteSpace: 'nowrap', zIndex: 1000, pointerEvents: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
                          {item.label}{item.badge > 0 && ` (${item.badge})`}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div style={{ padding: sidebarCollapsed ? '16px 0' : '16px', borderTop: `1px solid ${theme.sidebarBorder}`, background: currentTheme === 'blue' ? 'rgba(0,0,0,0.2)' : theme.filterSection, textAlign: sidebarCollapsed ? 'center' : 'left' }}>
            {!sidebarCollapsed ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: `linear-gradient(135deg, ${theme.accent}, ${theme.logoBg})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>{initials(employeeName)}</div>
                <div>
                  <div style={{ fontSize: '11px', color: theme.sidebarTextMuted }}>{clinicName}</div>
                  <div style={{ fontSize: '12px', color: theme.sidebarText, fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{employeeName}</div>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '14px', color: theme.sidebarText, cursor: 'pointer', position: 'relative' }} onMouseEnter={() => setHoveredNavItem('user')} onMouseLeave={() => setHoveredNavItem(null)}>
                👤
                {hoveredNavItem === 'user' && <div style={{ position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '12px', padding: '8px 12px', background: theme.tooltipBg, color: theme.tooltipText, fontSize: '12px', borderRadius: '6px', whiteSpace: 'nowrap', zIndex: 1000 }}>{employeeName} - Admin</div>}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main style={{ flex: 1, marginLeft: sidebarCollapsed ? '80px' : '260px', display: 'flex', flexDirection: 'column', minHeight: '100vh', transition: 'margin-left 0.3s ease', paddingTop: '80px' }}>
          <Topbar token={urlToken} themeColor={currentTheme} />

          <div style={{ background: theme.cardBg, borderBottom: `1px solid ${theme.cardBorder}`, padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: '60px', zIndex: 50 }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: theme.textPrimary }}>{activeView === 'list' ? 'SYSTEM ITEMS' : 'Create New Item'}</div>
            <div style={{ fontSize: '13px', color: theme.textMuted }}>{clinicName}</div>
          </div>

          <div style={{ flex: 1, padding: '28px' }}>
            {/* List View */}
            {activeView === 'list' && (
              <div style={styles(theme).card}>
                <div style={{ marginBottom: '20px' }}>
                  <input type="text" placeholder="🔍 Search items by name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ ...styles(theme).input, maxWidth: '400px' }} />
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles(theme).table}>
                    <thead>
                      <tr>
                        {['Item Name','Packaging','Warning Point','Cost Price','Selling Price','Actions'].map(h => <th key={h} style={styles(theme).th}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDrugs.length === 0 ? (
                        <tr><td colSpan="6" style={{ ...styles(theme).td, textAlign: 'center', padding: '40px', color: theme.textMuted }}>{allDrugs.length === 0 ? 'No items. Click "Create New Item" to get started.' : 'No items match your search.'}</td></tr>
                      ) : (
                        filteredDrugs.map((drug, i) => (
                          <tr key={drug.drug_id || i} className="table-row">
                            <td style={styles(theme).td}>{drug.drug_name}</td>
                            <td style={styles(theme).td}>{drug.packaging}</td>
                            <td style={styles(theme).td}>{drug.warning_point}</td>
                            <td style={styles(theme).td}>UGX {Number(drug.cost_price).toLocaleString()}</td>
                            <td style={styles(theme).td}>UGX {Number(drug.selling_price).toLocaleString()}</td>
                            <td style={styles(theme).td}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => handleOpenModal(drug)} style={{ ...styles(theme).actionButton, background: theme.infoLight, color: theme.info, border: `1px solid ${theme.info}` }} onMouseEnter={e => { e.currentTarget.style.background = theme.info; e.currentTarget.style.color = '#fff'; }} onMouseLeave={e => { e.currentTarget.style.background = theme.infoLight; e.currentTarget.style.color = theme.info; }}>✏️ Edit</button>
                                <button onClick={() => handleDeleteClick(drug)} style={{ ...styles(theme).actionButton, background: theme.dangerLight, color: theme.danger, border: `1px solid ${theme.danger}` }} onMouseEnter={e => { e.currentTarget.style.background = theme.danger; e.currentTarget.style.color = '#fff'; }} onMouseLeave={e => { e.currentTarget.style.background = theme.dangerLight; e.currentTarget.style.color = theme.danger; }}>🗑️ Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Create View */}
            {activeView === 'create' && (
              <div style={{ display: 'flex', gap: '24px' }}>
                <div className="create-mode" style={{ ...styles(theme).card, flex: 2 }}>
                  <div style={{ marginBottom: '24px' }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: theme.textPrimary, marginBottom: '8px' }}>Create New Item</h2>
                    <p style={{ color: theme.textMuted, fontSize: '14px' }}>Fill in the details below to add a new item to the system. It will be available across all business modules.</p>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                    {[
                      { label: 'Item Name', name: 'drugName', type: 'text', placeholder: 'e.g., Colgate, Soda' },
                      null, // packaging select goes here
                      { label: 'Warning Point', name: 'warningPoint', type: 'number', placeholder: 'Minimum stock alert level' },
                      { label: 'Cost Price (UGX)', name: 'costPrice', type: 'number', placeholder: 'Purchase price per unit' },
                      { label: 'Selling Price (UGX)', name: 'sellingPrice', type: 'number', placeholder: 'Selling price' },
                    ].map((field, idx) => {
                      if (idx === 1) return (
                        <div key="packaging">
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textMuted, marginBottom: '6px' }}>Packaging <span style={{ color: theme.danger }}>*</span></label>
                          <select name="packaging" value={newDrug.packaging} onChange={handleInputChange} style={styles(theme).select}>
                            <option value="">Select Packaging</option>
                            {['Boxes','Packets','Sackets','Pieces','Rolls','Bottles','kgs','ltrs'].map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                      );
                      if (!field) return null;
                      return (
                        <div key={field.name}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: theme.textMuted, marginBottom: '6px' }}>{field.label} <span style={{ color: theme.danger }}>*</span></label>
                          <input type={field.type} name={field.name} value={newDrug[field.name]} onChange={handleInputChange} placeholder={field.placeholder} style={styles(theme).input} />
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setActiveView('list')} style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${theme.cardBorder}`, background: 'transparent', color: theme.textSecondary, fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleCreateDrug} disabled={isAddingDrug} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: isAddingDrug ? theme.textMuted : theme.accent, color: '#fff', fontSize: '13px', fontWeight: '600', cursor: isAddingDrug ? 'not-allowed' : 'pointer', opacity: isAddingDrug ? 0.6 : 1 }}>
                      {isAddingDrug ? 'Creating...' : 'Create Item'}
                    </button>
                  </div>
                </div>

                {/* Info Panel */}
                <div style={{ flex: 1, background: theme.cardBg, border: `1px solid ${theme.cardBorder}`, borderRadius: '12px', padding: '24px', alignSelf: 'flex-start', position: 'sticky', top: '140px' }}>
                  <div style={{ width: '48px', height: '48px', background: theme.infoLight, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', fontSize: '24px' }}>ℹ️</div>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: theme.textPrimary, marginBottom: '12px' }}>About Item Creation</h3>
                  <p style={{ fontSize: '13px', color: theme.textSecondary, lineHeight: '1.6', marginBottom: '16px' }}>Creating a new item adds it to the system. It will become available across:</p>
                  {['Selling Items', 'Store inventory', 'Stock tracking'].map(txt => (
                    <div key={txt} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: theme.textSecondary, marginBottom: '10px' }}>
                      <span style={{ color: theme.accent, fontSize: '16px' }}>✓</span>{txt}
                    </div>
                  ))}
                  <div style={{ padding: '14px', background: theme.warningLight, border: `1px solid ${theme.warning}`, borderRadius: '8px', color: theme.warning, fontSize: '12px', lineHeight: '1.6', marginTop: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontWeight: '600' }}><span>⚠️</span> Administrator Notice</div>
                    <p style={{ margin: 0 }}>Ensure you have administrator privileges. Item name + packaging must be unique.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      <DeleteConfirmationModal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setDrugToDelete(null); }} onConfirm={handleConfirmDelete} drug={drugToDelete} theme={theme} isMobile={false} />
      {showModal && selectedDrug && (
        <UpdateStocksModal isOpen={showModal} onClose={() => { setShowModal(false); setSelectedDrug(null); }} selectedDrug={selectedDrug} token={token} refreshDrugs={() => fetchAllDrugs(token)} themeColor={currentTheme} />
      )}
    </>
  );
}

export default DrugOrigins;